import { SalePayload } from './types';

/**
 * How long one attempt is allowed to take before it is abandoned.
 *
 * The legacy component's `$.ajax` had no timeout at all, which is the failure worth
 * fixing here rather than a number worth tuning: a phone that opens the socket and then
 * loses signal never gets an error callback, so the spinner spins for ever, nothing is
 * reported, and the order is lost. Any finite value is better than none.
 */
const TIMEOUT_MS = 12000;

/** Attempts in total, not retries after the first. */
const ATTEMPTS = 3;

/** Backoff before attempt n (1-indexed), so 0ms, 500ms, 1500ms. */
const backoffMs = (attempt: number) => (attempt === 1 ? 0 : 500 * 2 ** (attempt - 2));

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class SaleLoadError extends Error {}

/**
 * True for a failure that another attempt might survive.
 *
 * A transport failure (no status at all) and a 5xx are the server or the network having
 * a bad moment. A 4xx is an answer — the sale is not there, or the url is wrong — and
 * repeating the request cannot change it. Neither can a payload that parsed as
 * something other than a sale.
 */
function isRetryable(error: unknown): boolean {
  return error instanceof SaleLoadError && /timed out|network|^5\d\d$/.test(error.message);
}

/**
 * A payload missing `categories` or `products` used to render as an empty sale at HTTP
 * 200 — indistinguishable, to anyone looking at it, from a sale with nothing in it. It
 * is a failure and is reported as one.
 */
function assertSale(data: unknown): SalePayload {
  const payload = data as Partial<SalePayload> | null;

  if (!payload || !Array.isArray(payload.categories) || !Array.isArray(payload.products)) {
    throw new SaleLoadError('The sale payload is missing its categories or products');
  }

  return { categories: payload.categories, products: payload.products };
}

async function attempt(url: string, signal?: AbortSignal): Promise<SalePayload> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  // The caller's signal (an unmount) and our own timeout both have to abort the same
  // request, and AbortSignal.any is too new to rely on here.
  const onAbort = () => controller.abort();
  signal?.addEventListener('abort', onAbort);

  try {
    // `same-origin`, not `omit`. Off DiscountMags the document comes from the site's own
    // origin, behind its WAF, and a request without the WAF's token cookie is answered with
    // a challenge -- 202 and an empty body -- instead of the sale. On DiscountMags the url is
    // the CDN, which is cross-origin, so `same-origin` sends no cookies there and the
    // request is as anonymous and as cacheable as it was.
    const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });

    // Only a 200 carries the sale. `response.ok` would let the WAF's 202 through, and the
    // empty body would then fail as a JSON syntax error rather than as the challenge it is.
    if (response.status !== 200) {
      throw new SaleLoadError(String(response.status));
    }

    return assertSale(await response.json());
  } catch (error) {
    if (error instanceof SaleLoadError) {
      throw error;
    }

    if (signal?.aborted) {
      throw new SaleLoadError('aborted');
    }

    throw new SaleLoadError(
      controller.signal.aborted ? `The sale request timed out after ${TIMEOUT_MS}ms` : 'network error'
    );
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
}

/**
 * Fetches the sale, bounded in both time and attempts.
 *
 * The url is the one the legacy page used and the payload is byte-identical, so this is
 * the same request against the same CDN object — what is new is that it can fail, and
 * say so, instead of hanging.
 */
export default async function loadSale(url: string, signal?: AbortSignal): Promise<SalePayload> {
  let lastError: unknown;

  for (let n = 1; n <= ATTEMPTS; n++) {
    if (signal?.aborted) {
      throw new SaleLoadError('aborted');
    }

    if (n > 1) {
      await sleep(backoffMs(n));
    }

    try {
      return await attempt(url, signal);
    } catch (error) {
      lastError = error;

      if (!isRetryable(error)) {
        throw error;
      }
    }
  }

  throw lastError;
}

/**
 * Where the sale lives.
 *
 * The server hands over the document's url — through the CDN or from the site's own
 * origin, see SaleDocumentUrl — so all that is left here is `previewMode`: the `xEO=Y`
 * that renders an expired sale, which has to travel on the JSON request too or the
 * payload comes back for the live (empty) sale.
 */
export function saleUrl(documentUrl: string, previewMode: boolean): string {
  return `${documentUrl}${previewMode ? '?xEO=Y' : ''}`;
}
